import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { History, Undo2, Redo2, RefreshCw, Search, Filter, ChevronRight, AlertCircle, CheckCircle2, Loader2, Eye, RotateCcw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  create: "bg-blue-50 text-blue-700",
  update: "bg-amber-50 text-amber-700",
  delete: "bg-red-50 text-red-700",
  bulk_delete: "bg-red-100 text-red-800",
  bulk_update: "bg-amber-100 text-amber-800",
  lock: "bg-emerald-50 text-emerald-700",
  unlock: "bg-orange-50 text-orange-700",
  revert: "bg-violet-50 text-violet-700",
};

const COLLECTION_LABELS = {
  invoices: "Invoice",
  invoice_batches: "Batch",
  adjustments: "Task",
  resource_mappings: "Resource",
  project_rates: "Project Rate",
  task_assignments: "Assignment",
  claims: "Claim",
};

const fmtTime = ts => {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AuditLog() {
  const [data, setData] = useState({ rows: [], total: 0, pending_reversible: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCollection, setFilterCollection] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reverting, setReverting] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (search) params.set("search", search);
      if (filterCollection) params.set("collection", filterCollection);
      if (filterAction) params.set("action", filterAction);
      if (showOnlyPending) params.set("only_pending", "true");
      const r = await axios.get(`${API}/audit-log?${params}`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filterCollection, filterAction, showOnlyPending]);
  useEffect(() => { const t = setTimeout(fetchData, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const handleRevert = async (row) => {
    if (!window.confirm(`Revert this action?\n\n${row.summary}\n\nThis will restore the previous state.`)) return;
    setReverting(row.id);
    try {
      const r = await axios.post(`${API}/audit-log/${row.id}/revert`, {}, { headers });
      toast.success(r.data.message || "Reverted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    } finally { setReverting(null); }
  };

  const handleUndoLast = async () => {
    setReverting("last");
    try {
      const r = await axios.post(`${API}/audit-log/undo-last`, {}, { headers });
      toast.success(r.data.summary ? `Undone: ${r.data.summary}` : (r.data.message || "Undone"));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to undo");
    } finally { setReverting(null); }
  };

  const handleRedoLast = async () => {
    setReverting("redo");
    try {
      const r = await axios.post(`${API}/audit-log/redo-last`, {}, { headers });
      toast.success(r.data.summary ? `Redone: ${r.data.summary}` : (r.data.message || "Redone"));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to redo");
    } finally { setReverting(null); }
  };

  const viewDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/audit-log/${id}`, { headers });
      setDetail(r.data);
    } catch { toast.error("Failed"); }
  };

  const collections = useMemo(() => Array.from(new Set(data.rows.map(r => r.collection))).sort(), [data.rows]);
  const actions = useMemo(() => Array.from(new Set(data.rows.map(r => r.action))).sort(), [data.rows]);

  return (
    <div className="p-6 max-w-full" data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><History className="w-6 h-6 text-violet-600" />Audit Log & Undo</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every delete/update across the app, with one-click revert</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="audit-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleUndoLast} disabled={reverting === "last" || data.pending_reversible === 0} data-testid="audit-undo-last">
            {reverting === "last" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}
            Undo Last Action {data.pending_reversible > 0 && <Badge className="ml-2 bg-white/20 text-white border-0">{data.pending_reversible}</Badge>}
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleRedoLast} disabled={reverting === "redo" || (data.pending_redo || 0) === 0} data-testid="audit-redo-last">
            {reverting === "redo" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Redo2 className="w-4 h-4 mr-1" />}
            Redo {(data.pending_redo || 0) > 0 && <Badge className="ml-2 bg-white/20 text-white border-0">{data.pending_redo}</Badge>}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-slate-600 text-xs font-semibold"><Filter className="w-3.5 h-3.5" />Filters</div>
            <div className="relative flex-1 min-w-[200px] max-w-[360px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input data-testid="audit-search" placeholder="Search summary, entity, user…" className="pl-8 h-8 text-xs" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select data-testid="audit-filter-collection" value={filterCollection} onChange={e => setFilterCollection(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              <option value="">All Entities</option>
              {Object.entries(COLLECTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              {collections.filter(c => !COLLECTION_LABELS[c]).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="audit-filter-action" value={filterAction} onChange={e => setFilterAction(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              <option value="">All Actions</option>
              {["create", "update", "delete", "lock", "unlock", "bulk_delete", "bulk_update", "revert"].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showOnlyPending} onChange={e => setShowOnlyPending(e.target.checked)} className="w-3.5 h-3.5 accent-violet-600" data-testid="audit-only-pending" />
              Reversible only
            </label>
            {(search || filterCollection || filterAction || showOnlyPending) && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFilterCollection(""); setFilterAction(""); setShowOnlyPending(false); }} className="text-xs text-slate-500"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm font-semibold">Recent Activity ({data.total})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.rows.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              <History className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              No audit entries match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">When</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Action</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Entity</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Summary</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">User</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`audit-row-${r.id}`}>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtTime(r.timestamp)}</td>
                      <td className="px-3 py-2"><Badge className={`${ACTION_COLORS[r.action] || "bg-slate-100 text-slate-700"} border-0 text-[10px] font-medium`}>{r.action}</Badge></td>
                      <td className="px-3 py-2 text-slate-700">{COLLECTION_LABELS[r.collection] || r.collection}</td>
                      <td className="px-3 py-2 text-slate-700 max-w-[420px] truncate" title={r.summary}>{r.summary}</td>
                      <td className="px-3 py-2 text-slate-600">{r.user}{r.user_role && r.user_role !== "unknown" ? <Badge className="ml-1.5 text-[9px] bg-slate-100 text-slate-500 border-0">{r.user_role}</Badge> : null}</td>
                      <td className="px-3 py-2 text-center">
                        {r.reverted ? (
                          <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px] gap-1"><RotateCcw className="w-2.5 h-2.5" />Reverted</Badge>
                        ) : !r.reversible ? (
                          <span className="text-[10px] text-slate-300">—</span>
                        ) : (
                          <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px] gap-1"><CheckCircle2 className="w-2.5 h-2.5" />Reversible</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-500" onClick={() => viewDetail(r.id)} data-testid={`audit-view-${r.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                          {r.reversible && !r.reverted && (
                            <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-violet-200 text-violet-700 hover:bg-violet-50" disabled={reverting === r.id} onClick={() => handleRevert(r)} data-testid={`audit-revert-${r.id}`}>
                              {reverting === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}Revert
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="audit-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-violet-600" />Audit Entry</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Time" value={fmtTime(detail.timestamp)} />
                <Field label="Action"><Badge className={`${ACTION_COLORS[detail.action] || "bg-slate-100"} border-0`}>{detail.action}</Badge></Field>
                <Field label="Entity" value={`${COLLECTION_LABELS[detail.collection] || detail.collection} · ${detail.entity_id || "—"}`} />
                <Field label="User" value={`${detail.user} (${detail.user_role})`} />
                <Field label="Summary" value={detail.summary} />
                <Field label="Status">
                  {detail.reverted ? <Badge className="bg-violet-100 text-violet-700 border-0">Reverted at {fmtTime(detail.reverted_at)} by {detail.reverted_by}</Badge> :
                    detail.reversible ? <Badge className="bg-emerald-50 text-emerald-700 border-0">Reversible</Badge> :
                    <Badge className="bg-slate-100 text-slate-600 border-0">Not reversible</Badge>}
                </Field>
              </div>
              {detail.before && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Before</p>
                  <pre className="bg-slate-50 border border-slate-100 rounded p-2 text-[10px] overflow-x-auto max-h-60">{JSON.stringify(detail.before, null, 2)}</pre>
                </div>
              )}
              {detail.after && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">After</p>
                  <pre className="bg-slate-50 border border-slate-100 rounded p-2 text-[10px] overflow-x-auto max-h-60">{JSON.stringify(detail.after, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
            {detail?.reversible && !detail?.reverted && (
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { handleRevert(detail); setDetail(null); }}><Undo2 className="w-4 h-4 mr-1" />Revert</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, children }) => (
  <div>
    <p className="text-[10px] font-semibold text-slate-500 uppercase">{label}</p>
    <div className="text-slate-700 text-xs mt-0.5">{children || value || "-"}</div>
  </div>
);
